import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  CreditCard, 
  TrendingUp, 
  TrendingDown,
  Coffee,
  Utensils,
  Car,
  Home,
  ShoppingBag,
  DollarSign,
  AlertTriangle,
  BookOpen,
  Target
} from "lucide-react";

/**
 * Transaction Insights Component
 * 
 * Displays recent financial transactions with educational insights
 * and recommendations to improve financial literacy through real spending data
 */

// Mock transaction data - replace with real transaction API
const mockTransactions = [
  {
    id: 1,
    amount: 45.67,
    merchant: "Starbucks Coffee",
    category: "dining",
    date: "2024-01-15",
    type: "debit",
    description: "Coffee & Pastry",
    insight: "You've spent $230 on coffee this month. Learn about the 'Latte Factor' in our budgeting lesson!",
    lessonSuggestion: "Smart Spending Habits",
    impactScore: 3
  },
  {
    id: 2,
    amount: 120.00,
    merchant: "Shell Gas Station",
    category: "transportation",
    date: "2024-01-14",
    type: "debit",
    description: "Fuel",
    insight: "Gas expenses are up 15% this month. Consider carpooling or public transport to save money.",
    lessonSuggestion: "Transportation Budgeting",
    impactScore: 2
  },
  {
    id: 3,
    amount: 89.23,
    merchant: "Whole Foods Market",
    category: "groceries",
    date: "2024-01-13",
    type: "debit",
    description: "Groceries",
    insight: "Great job staying within your grocery budget! You're 12% under your monthly limit.",
    lessonSuggestion: null,
    impactScore: 1
  },
  {
    id: 4,
    amount: 1200.00,
    merchant: "ABC Property Management",
    category: "housing",
    date: "2024-01-01",
    type: "debit",
    description: "Monthly Rent",
    insight: "Your housing costs are 28% of income - well within the recommended 30% rule!",
    lessonSuggestion: null,
    impactScore: 1
  },
  {
    id: 5,
    amount: 250.00,
    merchant: "Amazon Purchase",
    category: "shopping",
    date: "2024-01-12",
    type: "debit",
    description: "Electronics & Books",
    insight: "You've made 4 impulse purchases this week. Try the 24-hour rule before buying non-essentials.",
    lessonSuggestion: "Mindful Spending",
    impactScore: 3
  }
];

const TransactionInsights = () => {
  // Get category icon
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case "dining": return Coffee;
      case "transportation": return Car;
      case "groceries": return ShoppingBag;
      case "housing": return Home;
      case "shopping": return ShoppingBag;
      default: return CreditCard;
    }
  };

  // Get impact color based on score
  const getImpactColor = (score: number) => {
    if (score >= 3) return "text-red-600 bg-red-50 border-red-200";
    if (score === 2) return "text-yellow-600 bg-yellow-50 border-yellow-200";
    return "text-green-600 bg-green-50 border-green-200";
  };

  // Get impact text
  const getImpactText = (score: number) => {
    if (score >= 3) return "High Impact";
    if (score === 2) return "Medium Impact";
    return "Good Choice";
  };

  return (
    <Card className="bg-background border-gray-200 shadow-sm">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-secondary" />
            <CardTitle className="text-lg font-semibold text-gray-900">
              Smart Transaction Insights
            </CardTitle>
          </div>
          <Badge variant="secondary" className="bg-secondary/10 text-secondary">
            AI-Powered
          </Badge>
        </div>
        <p className="text-sm text-gray-600">
          Learn from your spending patterns to improve your financial health
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {mockTransactions.slice(0, 4).map((transaction) => {
            const CategoryIcon = getCategoryIcon(transaction.category);
            const impactColor = getImpactColor(transaction.impactScore);
            
            return (
              <div 
                key={transaction.id} 
                className="group p-4 border border-gray-100 rounded-lg hover:shadow-md transition-all duration-200 hover:border-secondary/20"
              >
                {/* Transaction Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gray-100 rounded-lg group-hover:bg-secondary/10 transition-colors">
                      <CategoryIcon className="h-4 w-4 text-gray-600 group-hover:text-secondary" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{transaction.merchant}</p>
                      <p className="text-sm text-gray-500 capitalize">
                        {transaction.category} • {new Date(transaction.date).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900 flex items-center gap-1">
                      <DollarSign className="h-3 w-3" />
                      {transaction.amount.toFixed(2)}
                    </p>
                    <Badge 
                      variant="outline" 
                      className={`text-xs ${impactColor} border`}
                    >
                      {getImpactText(transaction.impactScore)}
                    </Badge>
                  </div>
                </div>

                {/* AI Insight */}
                <div className="bg-gray-50 rounded-lg p-3 border-l-4 border-secondary/30">
                  <div className="flex items-start gap-2">
                    <div className="p-1 bg-secondary/10 rounded mt-0.5">
                      {transaction.impactScore >= 3 ? (
                        <AlertTriangle className="h-3 w-3 text-red-600" />
                      ) : (
                        <TrendingUp className="h-3 w-3 text-secondary" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-700 font-medium">AI Insight:</p>
                      <p className="text-sm text-gray-600 mt-1">{transaction.insight}</p>
                      
                      {/* Lesson Suggestion */}
                      {transaction.lessonSuggestion && (
                        <div className="mt-3 flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="h-7 px-3 text-xs bg-secondary/5 border-secondary/20 hover:bg-secondary/10"
                          >
                            <BookOpen className="h-3 w-3 mr-1" />
                            Learn: {transaction.lessonSuggestion}
                          </Button>
                          <Badge variant="secondary" className="text-xs bg-green-100 text-green-800">
                            +25 Centse Points
                          </Badge>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Action Footer */}
        <div className="mt-6 pt-4 border-t border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Target className="h-4 w-4" />
              <span>4 insights this week • 2 learning opportunities</span>
            </div>
            <Button variant="outline" size="sm" className="bg-secondary/5 border-secondary/20 hover:bg-secondary/10">
              View All Transactions
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TransactionInsights;